import { ReactElement, useCallback, useMemo, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRepeat as RenovateIcon,
  faDolly as DepositsIcon,
  faFileInvoiceDollar as BillingIcon,
} from '@fortawesome/free-solid-svg-icons';

import {
  IntegrationProvidersTypes,
  PlanFeaturesTypes,
} from '~globals/types/enums';
import { AdminIntegration } from '~services/admin/integration/types';
import { useAppSelector } from '~hooks/useAppSelector';
import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { selectAuth } from '~store/slices/auth';
import MenuActionsDataTable from '~components/MenuActionsDataTable';
import { DeleteIcon } from '~components/MenuActionsDataTable/styles';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { getFeatureAvailableToDate } from '~utils/feature';
import { PATHS } from '~constants/paths';

import { useIntegrationsSectionContext } from '../Context';

export interface IntegrationsSectionDataTableActionsMenuProps {
  data: AdminIntegration;
}

const IntegrationsSectionDataTableActionsMenu = ({
  data,
}: IntegrationsSectionDataTableActionsMenuProps): ReactElement => {
  const { user } = useAppSelector(selectAuth);
  const { isCarrier } = useTypePartnerCompany();

  const isValidUser = useMemo(
    () => !isOnlyReadUser(user) && !isOnlyCreatorOrder(user),
    [user],
  );

  const {
    dialogs: {
      renovate: { handleOpen: handleOpenDialogRenovateIntegration },
      assignWarehouses: {
        handleOpen: handleOpenDialogIntegrationAssignWarehouses,
      },
      assignBillingList: { handleOpen: handleOpenDialogAssignBillingList },
      delete: { handleOpen: handleOpenDialogConfirmDeleteIntegration },
    },
  } = useIntegrationsSectionContext();

  const handleClickRenovate = useCallback(() => {
    handleOpenDialogRenovateIntegration({
      integrationTypeId: data.integrationTypeId,
      companyIntegrationId: data.companyIntegrationId,
      description: data.description,
      storeId: data.storeId,
      carrierName: data.carrierName,
    });
  }, [data, handleOpenDialogRenovateIntegration]);

  const isEnabledToAssignWarehouses = useMemo(
    () =>
      isValidUser &&
      data.integrationTypeId === IntegrationProvidersTypes.MercadoLibre,
    [isValidUser, data.integrationTypeId],
  );
  const handleClickAssignWarehouses = useCallback(() => {
    handleOpenDialogIntegrationAssignWarehouses(data.companyIntegrationId);
  }, [data.companyIntegrationId, handleOpenDialogIntegrationAssignWarehouses]);

  const isEnabledToAssignBillingList = useMemo(
    () => isValidUser && isCarrier,
    [isValidUser, isCarrier],
  );
  const handleClickAssignBillingList = useCallback(() => {
    handleOpenDialogAssignBillingList(data);
  }, [data, handleOpenDialogAssignBillingList]);

  const handleClickDelete = useCallback(() => {
    handleOpenDialogConfirmDeleteIntegration(data);
  }, [data, handleOpenDialogConfirmDeleteIntegration]);

  return (
    <MenuActionsDataTable
      resource={{ path: PATHS.CONFIG.INTEGRATIONS.BASE }}
      items={{
        show: { enabled: false },
        edit: { enabled: false },
        delete: { enabled: false },
      }}
      extraItems={[
        {
          enabled: isValidUser,
          label: 'Renovar',
          Icon: <FontAwesomeIcon icon={RenovateIcon} />,
          onClick: handleClickRenovate,
          divider: true,
        },
        {
          enabled: isEnabledToAssignWarehouses,
          label: 'Depósitos de origen',
          Icon: <FontAwesomeIcon icon={DepositsIcon} />,
          onClick: handleClickAssignWarehouses,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitPaid,
          },
          divider: true,
        },
        {
          enabled: isEnabledToAssignBillingList,
          label: 'Asignar lista de precios',
          Icon: <FontAwesomeIcon icon={BillingIcon} />,
          onClick: handleClickAssignBillingList,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmBillingGiver,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmBillingGiver,
            ),
          },
          divider: true,
        },
        {
          enabled: isValidUser,
          label: 'Eliminar',
          Icon: <DeleteIcon />,
          onClick: handleClickDelete,
        },
      ]}
    />
  );
};

export default memo(IntegrationsSectionDataTableActionsMenu);
